// External
import { useContext, useState, useRef } from 'react';
import { LoadingButton } from '@mui/lab';
import { Button } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import CryptoJS from 'crypto-js';
import ReCAPTCHA from 'react-google-recaptcha';

// Project
import PATHS from 'src/config/paths';
import { TextInput } from 'src/components/Form';
import { useLocalAppUser } from 'src/hooks';
import { configs } from 'src/config';

// Features
import { CognitoContext } from '../providers';

const SmsMfa = () => {
  const navigate = useNavigate();
  const { appUser } = useParams();
  const { setLocalAppUser } = useLocalAppUser();
  const { cognitoObj, updateCognitoObj } = useContext(CognitoContext);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [showRecaptcha, setShowRecaptcha] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setShowRecaptcha(false);
    setShowResendConfirm(false);
    setLoadingConfirm(true);

    try {
      await Auth.confirmSignIn(cognitoObj, code, 'SMS_MFA');

      sessionStorage.clear();
      updateCognitoObj(undefined);
      setLocalAppUser(appUser === 'customer' ? 'customer' : 'agency');

      const currentUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const { attributes } = currentUser;

      if (attributes.email_verified) {
        navigate(PATHS.linkKeiki);
      } else {
        navigate(PATHS.verifyEmail);
      }
    } catch (err) {
      console.log('ConfirmError', err);
      setError('Invalid code. Please try again');
    } finally {
      setLoadingConfirm(false);
    }
  };

  const handleChange = (e) => setCode(e.target.value);

  const handleResendClick = () => {
    setShowRecaptcha(true);
    setError('');
    setShowResendConfirm(false);
  };

  // Called when user completes ReCAPTCHA
  const handleRecaptchaChange = async (token: string | null) => {
    if (!token) return;

    setLoadingResend(true);
    setError('');

    try {
      const email = cognitoObj.username;
      const encryptedPassword = sessionStorage.getItem(`encrypted-code.${email}`);
      const key = sessionStorage.getItem(`encrypt-key.${email}`);
      const password = CryptoJS.AES.decrypt(encryptedPassword, key).toString(CryptoJS.enc.Utf8);
      const user = await Auth.signIn(email, password, { email, captchaToken: token });
      updateCognitoObj(user);
      setShowResendConfirm(true);
    } catch (err) {
      console.log('ResendError', err);
      setError('System error. Try again in a bit.');
    } finally {
      setLoadingResend(false);
    }
  };

  return (
    <>
      <h2 className="text-center">Confirm Code</h2>
      <p className="mb-3">
        An authentication code has been sent to your phone number. Please enter the code below.
      </p>
      <form onSubmit={handleConfirm}>
        <TextInput
          autoComplete="off"
          name="code"
          label="Code"
          value={code}
          placeholder="Authentication Code"
          required
          onChange={handleChange}
          error={!!error}
        />
        {error && <div className="text-red-600">{error}</div>}
        {showResendConfirm && (
          <div className="text-green-600">Verification code has been resent!</div>
        )}
        <LoadingButton
          loading={loadingConfirm}
          variant="contained"
          className="mt-5 w-full normal-case"
          type="submit"
        >
          <span>Confirm</span>
        </LoadingButton>
      </form>
      <div className="mt-3 flex justify-between items-center">
        <Link to={PATHS.signIn('customer')} className="text-primary no-underline">
          Back to Sign In
        </Link>
        <Button
          className="p-2 text-base font-normal"
          onClick={handleResendClick}
          disabled={loadingResend}
        >
          {loadingResend ? 'Resending...' : 'Resend Code'}
        </Button>
      </div>
      {showRecaptcha && (
        <div className="flex flex-col items-center">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={configs.RECAPTCHA_SITE_KEY}
            onChange={handleRecaptchaChange}
          />
          Please complete the ReCAPTCHA to resend the code.
        </div>
      )}
    </>
  );
};

export default SmsMfa;
